/**
 * Strategy Engine Service
 * Connects to the Cloudflare Worker Strategy Engine API
 * Replaces local storage with cloud-persisted strategies
 */

import { API } from "./API";

// Strategy Engine API base URL (Cloudflare Worker)
const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  "https://strategy-engine.houman-ghavamzadeh.workers.dev";

// v4.9.3 Phase 1: Helper to build auth headers for direct fetch calls
function getAuthHeaders(extra = {}) {
  const token = localStorage.getItem("auth_token");
  return {
    "Content-Type": "application/json",
    ...(token ? { "Authorization": `Bearer ${token}` } : {}),
    "X-User-Id": localStorage.getItem("user_id") || "default-user",
    ...extra,
  };
}

// ============================================================================
// REQUEST MANAGEMENT - Prevents ERR_INSUFFICIENT_RESOURCES
// ============================================================================

// Track in-flight requests to prevent duplicates
const inFlightRequests = new Map();

// Global request queue to limit concurrent requests
const MAX_CONCURRENT_REQUESTS = 6;
let activeRequests = 0;
const requestQueue = [];

// Circuit breaker state
const circuitBreaker = {
  failures: 0,
  lastFailure: 0,
  isOpen: false,
  threshold: 8, // Open circuit after 8 failures
  resetTimeout: 30000, // Try again after 30 seconds
};

// Response cache for graceful degradation
const responseCache = new Map();
const CACHE_TTL = 30000; // Cache responses for 30 seconds

// Backoff state per endpoint
const backoffState = new Map();

/**
 * Get backoff delay for an endpoint
 */
const getBackoffDelay = (path) => {
  const state = backoffState.get(path);
  if (!state) return 0;

  const timeSinceLastFailure = Date.now() - state.lastFailure;
  const delay = Math.min(state.delay, 60000); // Max 60 second backoff

  if (timeSinceLastFailure < delay) {
    return delay - timeSinceLastFailure;
  }
  return 0;
};

/**
 * Record a failure for backoff calculation
 */
const recordFailure = (path) => {
  const state = backoffState.get(path) || { delay: 1000, lastFailure: 0 };
  state.delay = Math.min(state.delay * 2, 60000); // Exponential backoff, max 60s
  state.lastFailure = Date.now();
  backoffState.set(path, state);

  // Update circuit breaker
  circuitBreaker.failures++;
  circuitBreaker.lastFailure = Date.now();
  if (circuitBreaker.failures >= circuitBreaker.threshold) {
    circuitBreaker.isOpen = true;
    console.warn(
      "[StrategyEngineService] Circuit breaker OPEN - API requests paused",
    );
  }
};

/**
 * Record a success - reset backoff
 */
const recordSuccess = (path) => {
  backoffState.delete(path);
  circuitBreaker.failures = Math.max(0, circuitBreaker.failures - 1);
  if (circuitBreaker.failures === 0) {
    circuitBreaker.isOpen = false;
  }
};

/**
 * Check if circuit breaker allows requests
 */
const isCircuitOpen = () => {
  if (!circuitBreaker.isOpen) return false;

  // Check if we should try to close the circuit
  if (Date.now() - circuitBreaker.lastFailure > circuitBreaker.resetTimeout) {
    circuitBreaker.isOpen = false;
    circuitBreaker.failures = 0;
    console.log(
      "[StrategyEngineService] Circuit breaker CLOSED - resuming requests",
    );
    return false;
  }

  return true;
};

/**
 * Get cached response if available
 */
const getCachedResponse = (path) => {
  const cached = responseCache.get(path);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }
  return null;
};

/**
 * Cache a response
 */
const cacheResponse = (path, data) => {
  responseCache.set(path, { data, timestamp: Date.now() });
};

/**
 * Process request queue
 */
const processQueue = () => {
  while (requestQueue.length > 0 && activeRequests < MAX_CONCURRENT_REQUESTS) {
    const { resolve, reject, fn } = requestQueue.shift();
    activeRequests++;
    fn()
      .then(resolve)
      .catch(reject)
      .finally(() => {
        activeRequests--;
        processQueue();
      });
  }
};

/**
 * Queue a request to limit concurrency
 */
const queueRequest = (fn) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ resolve, reject, fn });
    processQueue();
  });
};

// Create axios instance for strategy engine
const strategyEngineAPI = {
  async request(method, path, data = null, options = {}) {
    // Create request key for deduplication and caching
    const requestKey = `${method}:${path}:${JSON.stringify(data || {})}`;
    const cacheKey = `${method}:${path}`;

    // Check circuit breaker - return cached data if available
    if (isCircuitOpen()) {
      const cached = getCachedResponse(cacheKey);
      if (cached) {
        console.log(
          `[StrategyEngineService] Circuit open, returning cached data for ${path}`,
        );
        return cached;
      }
      // Return empty array for list endpoints, empty object otherwise
      console.warn(
        `[StrategyEngineService] Circuit open, no cache for ${path}`,
      );
      return path.includes("?") || path.endsWith("s") ? [] : {};
    }

    // Check backoff - return cached data if available
    const backoffDelay = getBackoffDelay(path);
    if (backoffDelay > 0) {
      const cached = getCachedResponse(cacheKey);
      if (cached) {
        return cached;
      }
      return path.includes("?") || path.endsWith("s") ? [] : {};
    }

    // Check if request is already in flight
    if (inFlightRequests.has(requestKey)) {
      return inFlightRequests.get(requestKey);
    }

    // Queue the actual request to limit concurrency
    const requestPromise = queueRequest(async () => {
      const url = `${STRATEGY_ENGINE_URL}${path}`;
      // v4.9.3 Phase 1: Send JWT Bearer token from auth context
      const authToken = localStorage.getItem("auth_token");
      const headers = {
        "Content-Type": "application/json",
        ...(authToken ? { "Authorization": `Bearer ${authToken}` } : {}),
        "X-User-Id": localStorage.getItem("user_id") || "default-user",
        ...options.headers,
      };

      try {
        const controller = new AbortController();
        // v4.9.9: Longer timeout for heavy endpoints (positions/all fetches prices from KuCoin)
        const isHeavyEndpoint = path.includes('positions/all') || path.includes('bulk-close');
        const timeoutId = setTimeout(() => controller.abort(), isHeavyEndpoint ? 25000 : 10000);

        const response = await fetch(url, {
          method,
          headers,
          body: data ? JSON.stringify(data) : undefined,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        const result = await response.json();

        if (!response.ok) {
          // Auto-redirect to login on expired/invalid token
          if (response.status === 401 && (result.error || '').includes('expired')) {
            console.warn('[AUTH] Token expired, redirecting to login...');
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user_id');
            window.location.href = '/login';
            return {};
          }
          throw new Error(result.error || "Request failed");
        }

        recordSuccess(path);
        cacheResponse(cacheKey, result); // Cache successful response
        return result;
      } catch (error) {
        recordFailure(path);
        console.error(`Strategy Engine API error: ${method} ${path}`, error);

        // Return cached data on error if available
        const cached = getCachedResponse(cacheKey);
        if (cached) {
          console.log(
            `[StrategyEngineService] Error, returning cached data for ${path}`,
          );
          return cached;
        }

        // Return empty data instead of throwing to prevent cascade failures
        return path.includes("?") || path.endsWith("s") ? [] : {};
      } finally {
        // Remove from in-flight after a delay to prevent immediate re-requests
        setTimeout(() => inFlightRequests.delete(requestKey), 1000);
      }
    });

    // Store the promise for deduplication
    inFlightRequests.set(requestKey, requestPromise);

    return requestPromise;
  },

  get(path, options) {
    return this.request("GET", path, null, options);
  },

  post(path, data, options) {
    return this.request("POST", path, data, options);
  },

  put(path, data, options) {
    return this.request("PUT", path, data, options);
  },

  patch(path, data, options) {
    return this.request("PATCH", path, data, options);
  },

  delete(path, options) {
    return this.request("DELETE", path, null, options);
  },

  /**
   * Clear cached responses and in-flight dedup for a given path pattern.
   * Call after mutations (e.g. toggle) to ensure next GET fetches fresh data.
   */
  clearCache(pathPattern) {
    for (const [key] of responseCache) {
      if (key.includes(pathPattern)) responseCache.delete(key);
    }
    for (const [key] of inFlightRequests) {
      if (key.includes(pathPattern)) inFlightRequests.delete(key);
    }
  },
};

const StrategyEngineService = {
  baseUrl: STRATEGY_ENGINE_URL,
  // =========================================================================
  // STRATEGY CRUD
  // =========================================================================

  /**
   * List all strategies for the current user
   */
  async listStrategies(options = {}) {
    const params = new URLSearchParams();
    if (options.status) params.append("status", options.status);
    if (options.search) params.append("search", options.search);
    if (options.limit) params.append("limit", options.limit.toString());
    if (options.offset) params.append("offset", options.offset.toString());

    const queryString = params.toString();
    const path = `/api/strategies${queryString ? `?${queryString}` : ""}`;

    try {
      const result = await strategyEngineAPI.get(path);
      return result.data || [];
    } catch (error) {
      console.error("Error listing strategies:", error);
      // Return mock data for demo if API not available
      return this.getMockStrategies();
    }
  },

  /**
   * Get a strategy by ID
   */
  async getStrategy(strategyId) {
    // Use direct fetch to bypass circuit breaker — strategy switching must always work
    const url = `${STRATEGY_ENGINE_URL}/api/strategies/${strategyId}`;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to get strategy');
      return result.data;
    } catch (error) {
      console.error('Error getting strategy:', error);
      return null;
    }
  },

  /**
   * Create a new strategy
   * Note: Uses direct fetch to properly propagate errors
   */
  async createStrategy(strategyData) {
    const url = `${STRATEGY_ENGINE_URL}/api/strategies`;
    
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          name: strategyData.name,
          description: strategyData.description,
          nodes: strategyData.nodes || [],
          edges: strategyData.edges || [],
          variables: strategyData.variables || {},
          settings: strategyData.settings || {},
          tags: strategyData.tags || [],
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to create strategy");
      }

      // Backend returns { success: true, data: { id, ... } }
      if (result && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error("Error creating strategy:", error);
      throw error;
    }
  },

  /**
   * Update an existing strategy
   */
  async updateStrategy(strategyId, strategyData) {
    try {
      const result = await strategyEngineAPI.put(
        `/api/strategies/${strategyId}`,
        {
          name: strategyData.name,
          description: strategyData.description,
          nodes: strategyData.nodes,
          edges: strategyData.edges,
          variables: strategyData.variables,
          settings: strategyData.settings,
          tags: strategyData.tags,
          changeSummary: strategyData.changeSummary,
        },
      );
      // Backend returns { success: true, data: { ... } }
      const responseData = result.data;
      if (responseData && responseData.data) {
        return responseData.data;
      }
      return responseData;
    } catch (error) {
      console.error("Error updating strategy:", error);
      throw error;
    }
  },

  /**
   * Update strategy status
   */
  async updateStrategyStatus(strategyId, status) {
    try {
      await strategyEngineAPI.patch(`/api/strategies/${strategyId}/status`, {
        status,
      });
      return true;
    } catch (error) {
      console.error("Error updating strategy status:", error);
      return false;
    }
  },

  /**
   * Delete a strategy
   */
  async deleteStrategy(strategyId) {
    try {
      await strategyEngineAPI.delete(`/api/strategies/${strategyId}`);
      return true;
    } catch (error) {
      console.error("Error deleting strategy:", error);
      return false;
    }
  },

  /**
   * Get strategy versions
   */
  async getStrategyVersions(strategyId) {
    try {
      const result = await strategyEngineAPI.get(
        `/api/strategies/${strategyId}/versions`,
      );
      return result.data || [];
    } catch (error) {
      console.error("Error getting strategy versions:", error);
      return [];
    }
  },

  // =========================================================================
  // STRATEGY TEMPLATES & CLONING
  // =========================================================================

  /**
   * Save a strategy as a reusable template (stored in KV)
   */
  async saveAsTemplate(strategyId, templateName, templateDescription = "") {
    const url = `${STRATEGY_ENGINE_URL}/api/strategies/${strategyId}/save-template`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: templateName, description: templateDescription }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to save template");
      return result.data;
    } catch (error) {
      console.error("Error saving template:", error);
      throw error;
    }
  },

  /**
   * List all saved templates
   */
  async listTemplates() {
    try {
      const result = await strategyEngineAPI.get("/api/strategies/templates/list");
      return result.data?.data || result.data || [];
    } catch (error) {
      console.error("Error listing templates:", error);
      return [];
    }
  },

  /**
   * Get a template by ID (full data)
   */
  async getTemplate(templateId) {
    try {
      const result = await strategyEngineAPI.get(`/api/strategies/templates/${templateId}`);
      return result.data?.data || result.data;
    } catch (error) {
      console.error("Error getting template:", error);
      return null;
    }
  },

  /**
   * Delete a saved template
   */
  async deleteTemplate(templateId) {
    try {
      await strategyEngineAPI.delete(`/api/strategies/templates/${templateId}`);
      return true;
    } catch (error) {
      console.error("Error deleting template:", error);
      return false;
    }
  },

  /**
   * Create a new strategy from a saved template
   */
  async createFromTemplate(templateId, name = null) {
    const url = `${STRATEGY_ENGINE_URL}/api/strategies/from-template/${templateId}`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ name }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to create from template");
      return result.data;
    } catch (error) {
      console.error("Error creating from template:", error);
      throw error;
    }
  },

  /**
   * Clone an existing strategy
   */
  async cloneStrategy(strategyId, newName = null) {
    const url = `${STRATEGY_ENGINE_URL}/api/strategies/${strategyId}/clone`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: newName }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to clone strategy");
      return result.data;
    } catch (error) {
      console.error("Error cloning strategy:", error);
      throw error;
    }
  },

  // =========================================================================
  // PENDING SIGNALS (Confirmation Mode)
  // =========================================================================

  /**
   * List all pending signals awaiting user confirmation
   */
  async listPendingSignals() {
    try {
      const result = await strategyEngineAPI.get("/api/runs/pending-signals");
      return result.data?.data || result.data || [];
    } catch (error) {
      console.error("Error listing pending signals:", error);
      return [];
    }
  },

  /**
   * Confirm a pending signal — opens position at live price
   */
  async confirmSignal(signalId) {
    const url = `${STRATEGY_ENGINE_URL}/api/runs/pending-signals/${signalId}/confirm`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to confirm signal");
      return result.data;
    } catch (error) {
      console.error("Error confirming signal:", error);
      throw error;
    }
  },

  /**
   * Reject a pending signal
   */
  async rejectSignal(signalId) {
    const url = `${STRATEGY_ENGINE_URL}/api/runs/pending-signals/${signalId}/reject`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || "Failed to reject signal");
      return result;
    } catch (error) {
      console.error("Error rejecting signal:", error);
      throw error;
    }
  },

  // =========================================================================
  // RUN MANAGEMENT
  // =========================================================================

  /**
   * Start a strategy run
   * Note: This uses direct fetch to properly propagate errors
   */
  async startRun(strategyId, options = {}) {
    const url = `${STRATEGY_ENGINE_URL}/api/runs/strategies/${strategyId}/start`;
    
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          triggerType: options.triggerType || "manual",
          triggerData: options.triggerData || {},
          variables: options.variables || {},
          paperTrading: options.paperTrading !== false,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        // Propagate the actual error message from the backend
        const errorMessage = result.error || "Failed to start run";
        throw new Error(errorMessage);
      }

      // Backend returns { success: true, data: { runId, ... } }
      if (result && result.data) {
        return result.data;
      }
      return result;
    } catch (error) {
      console.error("Error starting run:", error);
      throw error;
    }
  },

  /**
   * Get run status
   */
  async getRunStatus(runId) {
    try {
      const result = await strategyEngineAPI.get(`/api/runs/${runId}`);
      return result.data;
    } catch (error) {
      console.error("Error getting run status:", error);
      return null;
    }
  },

  /**
   * List runs
   */
  async listRuns(options = {}) {
    const params = new URLSearchParams();
    if (options.strategyId) params.append("strategy_id", options.strategyId);
    if (options.status) params.append("status", options.status);
    if (options.limit) params.append("limit", options.limit.toString());

    const queryString = params.toString();
    const path = `/api/runs${queryString ? `?${queryString}` : ""}`;

    try {
      const result = await strategyEngineAPI.get(path);
      return result.data || [];
    } catch (error) {
      console.error("Error listing runs:", error);
      return [];
    }
  },

  /**
   * Pause a run
   */
  async pauseRun(runId) {
    try {
      await strategyEngineAPI.post(`/api/runs/${runId}/pause`);
      return true;
    } catch (error) {
      console.error("Error pausing run:", error);
      return false;
    }
  },

  /**
   * Resume a run
   */
  async resumeRun(runId) {
    try {
      await strategyEngineAPI.post(`/api/runs/${runId}/resume`);
      return true;
    } catch (error) {
      console.error("Error resuming run:", error);
      return false;
    }
  },

  /**
   * Stop a run
   */
  async stopRun(runId) {
    try {
      await strategyEngineAPI.post(`/api/runs/${runId}/stop`);
      return true;
    } catch (error) {
      console.error("Error stopping run:", error);
      return false;
    }
  },

  /**
   * Get run logs
   */
  async getRunLogs(runId, options = {}) {
    const params = new URLSearchParams();
    if (options.limit) params.append("limit", options.limit.toString());
    if (options.offset) params.append("offset", options.offset.toString());
    if (options.level) params.append("level", options.level);

    const queryString = params.toString();
    const path = `/api/runs/${runId}/logs${queryString ? `?${queryString}` : ""}`;

    try {
      const result = await strategyEngineAPI.get(path);
      return result.data || { logs: [], total: 0 };
    } catch (error) {
      console.error("Error getting run logs:", error);
      return { logs: [], total: 0 };
    }
  },

  /**
   * Connect to run WebSocket for live updates
   */
  connectToRunWebSocket(runId, callbacks = {}) {
    const wsUrl = `${STRATEGY_ENGINE_URL.replace("https://", "wss://").replace("http://", "ws://")}/api/runs/${runId}/ws`;

    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log(`Connected to run ${runId} WebSocket`);
      if (callbacks.onConnect) callbacks.onConnect();
    };

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        if (callbacks.onMessage) callbacks.onMessage(message);

        // Handle specific message types
        switch (message.type) {
          case "progress":
            if (callbacks.onProgress) callbacks.onProgress(message.data);
            break;
          case "log":
            if (callbacks.onLog) callbacks.onLog(message.data);
            break;
          case "completed":
            if (callbacks.onComplete) callbacks.onComplete(message.data);
            break;
          case "failed":
            if (callbacks.onError) callbacks.onError(message.data);
            break;
          case "paused":
            if (callbacks.onPause) callbacks.onPause(message.data);
            break;
          default:
            break;
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      // Don't call onError here — ws.onerror fires for transport failures,
      // not application-level run failures. Calling onError would conflate
      // a connection drop with the run actually failing (showing "Unknown error").
      // The onDisconnect callback handles reconnection/fallback instead.
    };

    ws.onclose = () => {
      console.log(`Disconnected from run ${runId} WebSocket`);
      if (callbacks.onDisconnect) callbacks.onDisconnect();

      // Fallback: poll run status once after WebSocket disconnect
      // to get the final state (completed/failed/running)
      setTimeout(async () => {
        try {
          const status = await StrategyEngineService.getRunStatus(runId);
          if (status) {
            if (status.status === 'failed' && callbacks.onError) {
              callbacks.onError({ error: status.error || 'Execution failed' });
            } else if (status.status === 'completed' && callbacks.onComplete) {
              callbacks.onComplete(status);
            }
          }
        } catch (e) {
          // Silently ignore polling errors
        }
      }, 2000);
    };

    return {
      close: () => ws.close(),
      send: (data) => ws.send(JSON.stringify(data)),
    };
  },

  // =========================================================================
  // ORDERS & TRADES
  // =========================================================================

  /**
   * Get orders
   */
  async getOrders(options = {}) {
    const params = new URLSearchParams();
    if (options.runId) params.append("run_id", options.runId);
    if (options.strategyId) params.append("strategy_id", options.strategyId);
    if (options.status) params.append("status", options.status);
    if (options.limit) params.append("limit", options.limit.toString());

    const queryString = params.toString();
    const path = `/api/orders${queryString ? `?${queryString}` : ""}`;

    try {
      const result = await strategyEngineAPI.get(path);
      return result.data || [];
    } catch (error) {
      console.error("Error getting orders:", error);
      return [];
    }
  },

  /**
   * Get trades
   */
  async getTrades(options = {}) {
    const params = new URLSearchParams();
    if (options.runId) params.append("run_id", options.runId);
    if (options.strategyId) params.append("strategy_id", options.strategyId);
    if (options.limit) params.append("limit", options.limit.toString());

    const queryString = params.toString();
    const path = `/api/trades${queryString ? `?${queryString}` : ""}`;

    try {
      const result = await strategyEngineAPI.get(path);
      return result.data || [];
    } catch (error) {
      console.error("Error getting trades:", error);
      return [];
    }
  },

  /**
   * Get positions
   */
  async getPositions(options = {}) {
    const params = new URLSearchParams();
    if (options.broker) params.append("broker", options.broker);
    if (options.accountId) params.append("account_id", options.accountId);

    const queryString = params.toString();
    const path = `/api/positions${queryString ? `?${queryString}` : ""}`;

    try {
      const result = await strategyEngineAPI.get(path);
      return result.data || [];
    } catch (error) {
      console.error("Error getting positions:", error);
      return [];
    }
  },

  /**
   * Get positions for a specific run
   */
  async getRunPositions(runId) {
    try {
      const result = await strategyEngineAPI.get(`/api/runs/${runId}/positions`);
      return result.data || [];
    } catch (error) {
      console.error("Error getting run positions:", error);
      return [];
    }
  },

  /**
   * Close a position
   */
  async closePosition(runId, positionId) {
    try {
      const result = await strategyEngineAPI.post(`/api/runs/${runId}/positions/${positionId}/close`);
      return result.data;
    } catch (error) {
      console.error("Error closing position:", error);
      throw error;
    }
  },

  /**
   * Update position settings (auto TP/SL toggles)
   */
  async updatePositionSettings(runId, positionId, settings) {
    try {
      const result = await strategyEngineAPI.patch(`/api/runs/${runId}/positions/${positionId}/settings`, settings);
      return result.data;
    } catch (error) {
      console.error("Error updating position settings:", error);
      throw error;
    }
  },

  /**
   * Manual budget add — add to an open position (same 4-level allocation as rescue)
   */
  async manualBudgetAdd(runId, positionId) {
    try {
      const result = await strategyEngineAPI.post(`/api/runs/${runId}/positions/${positionId}/manual-add`, {});
      return result.data;
    } catch (error) {
      console.error("Error executing manual budget add:", error);
      throw error;
    }
  },

  /**
   * Bulk close positions by P&L filter
   * @param {Object} filter - { type: 'PROFIT_ONLY'|'LOSS_ONLY'|'PROFIT_AT_LEAST'|'LOSS_AT_MOST', thresholdPct?: number }
   * @param {boolean} dryRun - If true, returns preview without executing
   * @param {boolean} reduceOnly - If true, uses reduce-only orders for futures
   */
  async bulkClosePositions(filter, dryRun = false, reduceOnly = true) {
    try {
      // Use direct fetch to bypass circuit breaker / error-swallowing for this critical operation
      const url = `${STRATEGY_ENGINE_URL}/api/runs/positions/bulk-close`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout for bulk ops

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ filter, dryRun, reduceOnly }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || result.message || `Bulk close failed (HTTP ${response.status})`);
      }

      return result;
    } catch (error) {
      console.error("Error bulk closing positions:", error);
      throw error;
    }
  },

  /**
   * Bulk update TP/SL settings for ALL open positions
   */
  async bulkUpdatePositionSettings(settings) {
    try {
      const result = await strategyEngineAPI.patch(`/api/runs/positions/bulk-settings`, settings);
      return result;
    } catch (error) {
      console.error("Error bulk updating position settings:", error);
      throw error;
    }
  },

  /**
   * Open a position manually from a signal
   */
  async openPosition(runId, positionData) {
    try {
      const result = await strategyEngineAPI.post(`/api/runs/${runId}/positions/open`, positionData);
      return result;
    } catch (error) {
      console.error("Error opening position:", error);
      throw error;
    }
  },

  /**
   * Get all positions across all runs
   */
  async getAllPositions() {
    try {
      const result = await strategyEngineAPI.get("/api/runs/positions/all");
      return result.data || [];
    } catch (error) {
      console.error("Error fetching all positions:", error);
      return [];
    }
  },

  // =========================================================================
  // SIGNALS (v4.4.1)
  // =========================================================================

  /**
   * Execute a signal — converts it to an open position at live price
   */
  async executeSignal(signalId) {
    try {
      const result = await strategyEngineAPI.post(`/api/admin/signal/${signalId}/execute`);
      return result.data;
    } catch (error) {
      console.error("Error executing signal:", error);
      throw error;
    }
  },

  /**
   * Dismiss a signal — removes it from the positions store
   */
  async dismissSignal(signalId) {
    try {
      const result = await strategyEngineAPI.post(`/api/admin/signal/${signalId}/dismiss`);
      return result.data;
    } catch (error) {
      console.error("Error dismissing signal:", error);
      throw error;
    }
  },

  // =========================================================================
  // SCHEDULES
  // =========================================================================

  /**
   * Schedule a strategy to run at intervals
   */
  async scheduleStrategy(strategyId, options = {}) {
    try {
      const result = await strategyEngineAPI.post(`/api/runs/strategies/${strategyId}/schedule`, options);
      return result.data;
    } catch (error) {
      console.error("Error scheduling strategy:", error);
      throw error;
    }
  },

  /**
   * Get schedule for a strategy
   */
  async getStrategySchedule(strategyId) {
    try {
      const result = await strategyEngineAPI.get(`/api/runs/strategies/${strategyId}/schedule`);
      return result.data;
    } catch (error) {
      console.error("Error getting strategy schedule:", error);
      return null;
    }
  },

  /**
   * Alias for getStrategySchedule - used by NodeSettingsPanel
   */
  async getSchedule(strategyId) {
    return this.getStrategySchedule(strategyId);
  },

  /**
   * Delete schedule for a strategy
   */
  async deleteStrategySchedule(strategyId) {
    try {
      await strategyEngineAPI.delete(`/api/runs/strategies/${strategyId}/schedule`);
      return true;
    } catch (error) {
      console.error("Error deleting strategy schedule:", error);
      return false;
    }
  },

  /**
   * Toggle paper/real trading mode for a strategy
   * @param {string} strategyId
   * @param {string} field - 'paperTrading' or 'realTrading'
   * @param {boolean} value
   */
  async toggleTradingMode(strategyId, field, value) {
    try {
      const result = await strategyEngineAPI.patch(`/api/schedules/${strategyId}/trading-mode`, { field, value });
      strategyEngineAPI.clearCache('execution-status');
      return result;
    } catch (error) {
      console.error("Error toggling trading mode:", error);
      throw error;
    }
  },

  /**
   * Toggle a strategy's schedule on/off (quick activate/deactivate)
   */
  async toggleSchedule(strategyId) {
    try {
      const result = await strategyEngineAPI.patch(`/api/schedules/${strategyId}/toggle`);
      // Clear execution-status cache so next fetch gets fresh toggle state
      strategyEngineAPI.clearCache('execution-status');
      return result;
    } catch (error) {
      console.error("Error toggling schedule:", error);
      throw error;
    }
  },

  /**
   * Get execution status for all active strategies
   * Returns detailed info about each running strategy including broker, trading mode, positions
   */
  async getExecutionStatus() {
    try {
      const result = await strategyEngineAPI.get("/api/strategies/execution-status");
      return result;
    } catch (error) {
      console.error("Error getting execution status:", error);
      return { strategies: [], conflicts: [] };
    }
  },

  // =========================================================================
  // EXIT SETTINGS
  // =========================================================================

  /**
   * Get current exit settings
   */
  async getExitSettings() {
    try {
      const result = await strategyEngineAPI.get("/api/runs/exit-settings");
      return result;
    } catch (error) {
      console.error("Error fetching exit settings:", error);
      return null;
    }
  },

  /**
   * Update exit settings
   */
  async updateExitSettings(settings) {
    try {
      const result = await strategyEngineAPI.post("/api/runs/exit-settings", settings);
      return result;
    } catch (error) {
      console.error("Error updating exit settings:", error);
      throw error;
    }
  },

  // =========================================================================
  // EXIT POLICY (v3.9.15) — Global + per-position TP/SL/AI toggles
  // =========================================================================

  /**
   * Update schedule-level exit policy (global TP/SL/AI toggles + AI exit mode)
   * Also propagates to all open positions immediately.
   */
  async updateExitPolicy(strategyId, policy) {
    try {
      const result = await strategyEngineAPI.patch(
        `/api/runs/exit-policy/${strategyId}`,
        policy,
      );
      return result;
    } catch (error) {
      console.error("Error updating exit policy:", error);
      throw error;
    }
  },

  /**
   * Update per-position exit overrides (TP/SL/AI toggles for a single position)
   */
  async updatePositionExitOverrides(runId, positionId, overrides) {
    try {
      const result = await strategyEngineAPI.patch(
        `/api/runs/${runId}/positions/${positionId}/settings`,
        overrides,
      );
      return result;
    } catch (error) {
      console.error("Error updating position exit overrides:", error);
      throw error;
    }
  },

  /**
   * Get available exit presets
   */
  async getExitPresets() {
    try {
      const result = await strategyEngineAPI.get("/api/runs/exit-presets");
      return result;
    } catch (error) {
      console.error("Error fetching exit presets:", error);
      return null;
    }
  },

  /**
   * Get schedules
   */
  async getSchedules(strategyId = null) {
    const path = strategyId
      ? `/api/schedules?strategy_id=${strategyId}`
      : "/api/schedules";

    try {
      const result = await strategyEngineAPI.get(path);
      return result.data || [];
    } catch (error) {
      console.error("Error getting schedules:", error);
      return [];
    }
  },

  /**
   * Create schedule
   */
  async createSchedule(scheduleData) {
    try {
      const result = await strategyEngineAPI.post(
        "/api/schedules",
        scheduleData,
      );
      return result.data;
    } catch (error) {
      console.error("Error creating schedule:", error);
      throw error;
    }
  },

  /**
   * Delete schedule
   */
  async deleteSchedule(scheduleId) {
    try {
      await strategyEngineAPI.delete(`/api/schedules/${scheduleId}`);
      return true;
    } catch (error) {
      console.error("Error deleting schedule:", error);
      return false;
    }
  },

  // =========================================================================
  // AI ANALYSIS METHODS
  // =========================================================================

  /**
   * Get AI optimal entry price analysis
   * Determines if a limit order at a better price is likely (>70% probability)
   */
  async getOptimalEntry(symbol, side) {
    try {
      const result = await strategyEngineAPI.get(
        `/api/ai/optimal-entry?symbol=${encodeURIComponent(symbol)}&side=${side}`
      );
      return result.data?.data || result.data;
    } catch (error) {
      console.error("Error getting optimal entry:", error);
      return null;
    }
  },

  /**
   * Get full AI trade analysis including quality score and optimal entry
   */
  async getTradeAnalysis(symbol, side) {
    try {
      const result = await strategyEngineAPI.get(
        `/api/ai/analyze-trade?symbol=${encodeURIComponent(symbol)}&side=${side}`
      );
      return result.data?.data || result.data;
    } catch (error) {
      console.error("Error getting trade analysis:", error);
      return null;
    }
  },

  /**
   * Open a limit order position at a specific price
   */
  async openLimitOrder(runId, orderData) {
    try {
      const result = await strategyEngineAPI.post(
        `/api/runs/${runId}/limit-order`,
        orderData
      );
      return result.data;
    } catch (error) {
      console.error("Error opening limit order:", error);
      throw error;
    }
  },

  // =========================================================================
  // MONEY MANAGEMENT
  // =========================================================================

  /**
   * Get money management settings for a run
   */
  async getMoneyManagementSettings(runId) {
    try {
      const result = await strategyEngineAPI.get(
        `/api/runs/${runId}/money-management`
      );
      return result.data?.data || result.data;
    } catch (error) {
      console.error("Error getting money management settings:", error);
      return null;
    }
  },

  /**
   * Update money management settings for a run
   */
  async updateMoneyManagementSettings(runId, settings) {
    try {
      const result = await strategyEngineAPI.post(
        `/api/runs/${runId}/money-management`,
        settings
      );
      return result.data;
    } catch (error) {
      console.error("Error updating money management settings:", error);
      throw error;
    }
  },

  /**
   * Get capital status for a run
   */
  async getCapitalStatus(runId) {
    try {
      const result = await strategyEngineAPI.get(
        `/api/runs/${runId}/capital-status`
      );
      return result.data?.data || result.data;
    } catch (error) {
      console.error("Error getting capital status:", error);
      return null;
    }
  },

  /**
   * Compute allocation for a potential trade
   */
  async computeAllocation(runId, { symbol, price, confidence, volatilityScore }) {
    try {
      const result = await strategyEngineAPI.post(
        `/api/runs/${runId}/compute-allocation`,
        { symbol, price, confidence, volatilityScore }
      );
      return result.data?.data || result.data;
    } catch (error) {
      console.error("Error computing allocation:", error);
      return null;
    }
  },

  // =========================================================================
  // MOCK DATA (fallback when API not available)
  // =========================================================================

  getMockStrategies() {
    return [
      {
        id: "mock-1",
        name: "BTC Trend Following",
        description: "Technical-based trend following for BTC/USD",
        status: "active",
        tags: ["crypto", "technical"],
        currentVersion: 1,
        nodes: [],
        edges: [],
        createdAt: "2025-05-15T00:00:00Z",
        updatedAt: "2025-05-15T00:00:00Z",
      },
      {
        id: "mock-2",
        name: "ETH News Reactive",
        description: "Fundamental-based strategy for ETH/USD",
        status: "draft",
        tags: ["crypto", "news", "sentiment"],
        currentVersion: 1,
        nodes: [],
        edges: [],
        createdAt: "2025-05-10T00:00:00Z",
        updatedAt: "2025-05-10T00:00:00Z",
      },
    ];
  },
};

export default StrategyEngineService;
